import type { TaskState } from '@app/types/tasks'

import moment from '@app/utils/moment'

import { withAbortSignal } from '@app/packages/abortContext/actions'
import type { TaskValue, TaskWorker } from '@app/packages/task/Task'
import { Task } from '@app/packages/task/Task'

import type { StoreDispatch } from '@app/store/dispatch'
import { getTaskSlice, setTaskSlice } from '@app/store/slices/tasks'
import type { ThunkAction } from '@app/store/thunk'
import { createThunk } from '@app/store/thunk'

export const createTask = <S, W extends TaskWorker>(s: string, taskCreator: (dispatch: StoreDispatch) => Task<S, W>) =>
  createThunk(dispatch => {
    const task = dispatch(getTaskSlice(s))
    if (task instanceof Task) return task as Task<S, W>
    if (isTaskState(task)) {
      const newTask = taskCreator(dispatch)
      newTask.state.update(() => task.state)
      newTask.value.update(() => task.value)
      newTask.loadedAt.update(() => (task.loadedAt ? moment(task.loadedAt) : null))
      dispatch(setTaskSlice(s, newTask))
      return newTask
    }
    const newTask = taskCreator(dispatch)
    dispatch(setTaskSlice(s, newTask))
    return newTask
  })

export function withTaskContext<T>(ctx: { abortController: AbortController }, p: ThunkAction<Promise<T>>) {
  return createThunk(dispatch => dispatch(withAbortSignal(ctx.abortController.signal, p)))
}

export async function updateTaskValue<T extends Task<any, () => Promise<any>>>(task: T, freshnessInMinutes?: number): Promise<TaskValue<T>> {
  if (task.loading.value) return await task.currentPromise.value
  if (!task.loadedAt.value) return await task.call()
  if (freshnessInMinutes && moment().diff(task.loadedAt.value, 'minutes') < freshnessInMinutes) return task.value.value
  return await task.call()
}

const isTaskState = (t: unknown): t is TaskState => {
  return !!(t && typeof t === 'object' && 'isTaskState' in t && t.isTaskState === true)
}
